import { gql, useQuery } from '@apollo/client';
import React, { useMemo } from 'react';
import { useRecoilCallback } from 'recoil';
import fragments from 'services/fragments';
import { Account } from 'types/Account';
import {
  SummaryTimeline,
  Timeline,
  TimelineTypeEnum,
  TimelineTypeFilterEnum,
} from 'types/timeline.types';
import { eventsAtom } from './eventsPage.atoms';
import useActionsParams from './useActionsParams';

const EVENTS = gql`
  ${fragments.Timeline}
  query Timeline(
    $isCloud: Boolean!
    $accountId: Int
    $userId: Int
    $email: String
    $hasPermissionToViewSO: Boolean
    $hasPermissionToViewOpenSO: Boolean
    $hasPermissionToViewOccurrenceLogs: Boolean
    $notificationMoment: String
    $timezone: Int
    $masterCompanyId: Int
    $beginDate: String
    $endDate: String
    $accountType: Int
  ) {
    Timeline(
      isCloud: $isCloud
      accountId: $accountId
      userId: $userId
      email: $email
      hasPermissionToViewSO: $hasPermissionToViewSO
      hasPermissionToViewOpenSO: $hasPermissionToViewOpenSO
      hasPermissionToViewOccurrenceLogs: $hasPermissionToViewOccurrenceLogs
      notificationMoment: $notificationMoment
      timezone: $timezone
      masterCompanyId: $masterCompanyId
      beginDate: $beginDate
      endDate: $endDate
      accountType: $accountType
    ) {
      ...TimelineProperties
    }
  }
`;

const EVENTS_ACCOUNTS_OVERVIEW = gql`
  ${fragments.Events}
  query GetAccountsOverviewEvents($beginDate: String!, $endDate: String!) {
    getAccountsOverviewEvents(beginDate: $beginDate, endDate: $endDate) {
      ...EventsProperties
    }
  }
`;

type TimelineGQL = { Timeline: Timeline[] };
type AccountTimeline = { getAccountsOverviewEvents: SummaryTimeline[] };

function useTimelineData({ accountSelected }: { accountSelected: Account | null }) {
  const { beginDate, endDate, eventType } = useActionsParams();
  const { data, error, loading } = useQuery<TimelineGQL | AccountTimeline>(
    accountSelected ? EVENTS : EVENTS_ACCOUNTS_OVERVIEW,
    {
      variables: {
        ...accountSelected,
        accountId: accountSelected?.id,
        beginDate,
        endDate,
        hasPermissionToViewSO: false,
        hasPermissionToViewOpenSO: false,
      },
    },
  );

  const listData = React.useMemo(() => {
    if (data) {
      return (data as TimelineGQL).Timeline || (data as AccountTimeline).getAccountsOverviewEvents;
    }
  }, [data]);

  const events = useMemo<Timeline[] | SummaryTimeline[] | undefined>(() => {
    if (eventType) {
      return listData?.filter((item) => {
        switch (Number(eventType)) {
          case TimelineTypeFilterEnum.ALL:
            return true;
          case TimelineTypeFilterEnum.ALARM || item.type === TimelineTypeEnum.PANIC:
            return item.type === TimelineTypeEnum.ALARM;
          case TimelineTypeFilterEnum.AUTOMATIC_TEST:
            return item.type === TimelineTypeEnum.TEST;
          case TimelineTypeFilterEnum.ARMED:
            return item.type === TimelineTypeEnum.ARM;
          case TimelineTypeFilterEnum.DISARMED:
            return item.type === TimelineTypeEnum.DISARM;
          case TimelineTypeFilterEnum.ARMED_DISARMED:
            return item.type === TimelineTypeEnum.ARM || item.type === TimelineTypeEnum.DISARM;
          case TimelineTypeFilterEnum.WITHOUT_CONTROL:
            return item.type === TimelineTypeEnum.NO_CONTROL;
          case TimelineTypeFilterEnum.PATROL:
            return item.type === TimelineTypeEnum.PATROL;

          default:
            return false;
        }
      });
    }

    return listData;
  }, [eventType, listData]);

  const setEvents = useRecoilCallback(
    ({ set }) =>
      (newData: Timeline[] | SummaryTimeline[] | undefined) => {
        set(eventsAtom, newData);
      },
  );

  React.useEffect(
    function setEventsWhenChangeData() {
      setEvents(events);
    },
    [events, setEvents],
  );

  if (error) {
    throw error;
  }

  return {
    data: events,
    loading,
  };
}

export default useTimelineData;
